import { useRef, useState } from "react";
import {
  Box,
  Button,
  Field,
  FieldError,
  FieldLabel,
  Input,
  ScrollArea,
  useIsMobile,
} from "@salestrak/ui";
import { PageHeader, PageHeaderSubtitle, PageHeaderTitle, SETTINGS_CONTENT_MAX_WIDTH, SettingsPageGutter, SettingsBackBar, SettingsBackBarClearance, } from "#app/components/page-header";
import { SettingsGroup, SettingsGroupContent } from "./settings-group";
import { PICKER_CONTROL_SELECTOR, focusFirstInvalid } from "#app/lib/utils/focus-first-invalid";
import { PermissionEdit } from "./permission-edit";
import { FULL_PERMISSIONS, type Permissions } from "./permission-badge";
import type { Omittable } from "@salestrak/ui";

type ApiKeyFormViewProps = Omittable<{
  apiKey?: {
    apiKeyId: string;
    name: string;
    permissions: Permissions;
  } | null;
  onCancel: () => void;
  onSubmit: (data: { name: string; permissions: Permissions }) => Promise<void>;
}>;

export function ApiKeyFormView({
  apiKey,
  onCancel,
  onSubmit,
}: ApiKeyFormViewProps) {
  const isEdit = !!apiKey;
  const isMobile = useIsMobile();
  const [name, setName] = useState(apiKey?.name ?? "");
  const [permissions, setPermissions] = useState<Permissions>(
    apiKey?.permissions ?? FULL_PERMISSIONS,
  );
  const [submitting, setSubmitting] = useState(false);
  // Errors show only after a blocked press, then clear as answers arrive.
  const [attempted, setAttempted] = useState(false);
  const nameFieldRef = useRef<HTMLDivElement>(null);
  const permissionsFieldRef = useRef<HTMLFieldSetElement>(null);

  const nameMissing = name.trim() === "";
  // The server refuses a key with no permission, because it could do nothing.
  const permissionMissing = !(
    permissions.all || permissions.read || permissions.create || permissions.update || permissions.delete
  );
  const nameError = attempted && nameMissing;
  const permissionError = attempted && permissionMissing;

  async function handleSubmit() {
    if (submitting) return;
    if (nameMissing || permissionMissing) {
      setAttempted(true);
      focusFirstInvalid([
        { root: nameFieldRef.current, selector: "input", invalid: nameMissing },
        { root: permissionsFieldRef.current, selector: PICKER_CONTROL_SELECTOR, invalid: permissionMissing },
      ]);
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({ name: name.trim(), permissions });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box direction="column" fill="flex" position="relative">
      <SettingsBackBar label="API" onClick={onCancel} />
      <ScrollArea>
        <SettingsBackBarClearance />
        <PageHeader centered pinnedBack>
          <PageHeaderTitle>{isEdit ? "Edit API key" : "Create API key"}</PageHeaderTitle>
          <PageHeaderSubtitle>
            Actions made with this key are attributed to you.
          </PageHeaderSubtitle>
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
          <Box
            as="form"
            gap="2xl"
            fill="width"
            maxWidth={SETTINGS_CONTENT_MAX_WIDTH}
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit();
            }}
          >
            <SettingsGroup label="Key">
              <SettingsGroupContent>
                <Box gap="md">
                  <Field invalid={nameError} ref={nameFieldRef}>
                    <FieldLabel required>Name</FieldLabel>
                    <Input
                      placeholder="Reporting script"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      autoFocus={!isMobile}
                    />
                    {nameError ? <FieldError match>Give the key a name.</FieldError> : null}
                  </Field>
                  <PermissionEdit
                    value={permissions}
                    onChange={setPermissions}
                    error={permissionError ? "Choose at least one permission" : null}
                    fieldRef={permissionsFieldRef}
                  />
                </Box>
              </SettingsGroupContent>
            </SettingsGroup>

            <Box direction="row" gap="sm" justify="end">
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {isEdit ? "Save changes" : "Create API key"}
              </Button>
            </Box>

            <SettingsPageGutter />
          </Box>
        </Box>
      </ScrollArea>
    </Box>
  );
}
